import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ClassStudentRecord } from '../../classes/mocks/classStudents';
import { ClassVisitorRecord } from '../../classes/mocks/classVisitors';
import { classes as classSeed } from '../../../data/modules';
import { AttendanceEntry } from '../../attendance/types/attendance.types';
import { buildLessonId, ClassLesson, LESSONS_PER_TRIMESTER } from '../../classes/types/classLessons';
import { useAuth } from '../../auth/context/AuthContext';
import { trimestersService } from '../services/trimestersService';
export type ClassRecord = (typeof classSeed)[number] & {
  segmentId?: string | null;
  teacherMemberIds?: string[];
  active?: boolean;
};

export interface Trimester {
  id: string;
  year: number;
  number: 1 | 2 | 3 | 4;
  label: string;
  startDate: string;
  endDate: string;
  active?: boolean;
  magazinesCount?: number;
  classQuartersCount?: number;
}

interface CreateTrimesterInput {
  year: number;
  number: 1 | 2 | 3 | 4;
  startDate: string;
  endDate: string;
  replicateFromId?: string;
  excludedStudentIds?: string[];
  replicationOptions?: {
    selectedClassIds?: string[];
    includeTeachers?: boolean;
    includeStudents?: boolean;
  };
}

interface TrimestersContextValue {
  trimesters: Trimester[];
  currentTrimesterId: string;
  currentTrimester: Trimester;
  setCurrentTrimesterId: (id: string) => void;
  createTrimester: (input: CreateTrimesterInput) => Promise<string>;
  updateTrimester: (id: string, updates: Partial<Omit<Trimester, 'id'>>) => Promise<void>;
  deleteTrimester: (id: string) => Promise<void>;
  isLoadingTrimesters: boolean;
  trimestersError: string | null;
  reloadTrimesters: () => Promise<void>;
  studentsByTrimester: Record<string, ClassStudentRecord[]>;
  getStudents: (trimesterId: string) => ClassStudentRecord[];
  classes: ClassRecord[];
  setClasses: React.Dispatch<React.SetStateAction<ClassRecord[]>>;
  createClass: (classItem: ClassRecord) => void;
  updateClass: (classId: string, updates: Partial<ClassRecord>) => void;
  removeClass: (classId: string) => void;
  students: ClassStudentRecord[];
  setStudents: React.Dispatch<React.SetStateAction<ClassStudentRecord[]>>;
  updateStudent: (studentId: string, updater: (student: ClassStudentRecord) => ClassStudentRecord) => void;
  visitorsByTrimester: Record<string, ClassVisitorRecord[]>;
  visitors: ClassVisitorRecord[];
  setVisitors: React.Dispatch<React.SetStateAction<ClassVisitorRecord[]>>;
  updateVisitor: (visitorId: string, updater: (visitor: ClassVisitorRecord) => ClassVisitorRecord) => void;
  removeReplicatedStudent: (trimesterId: string, studentId: string) => void;
  attendanceEntriesByTrimester: Record<string, AttendanceEntry[]>;
  attendanceEntries: AttendanceEntry[];
  setAttendanceEntries: React.Dispatch<React.SetStateAction<AttendanceEntry[]>>;
  classLessonsByTrimester: Record<string, Record<string, ClassLesson[]>>;
  getClassLessons: (classId: string, trimesterId?: string) => ClassLesson[];
  updateClassLesson: (classId: string, lessonId: string, updates: Partial<Omit<ClassLesson, 'id' | 'classId' | 'lessonNumber'>>) => void;
  sourceTrimesterClasses: ClassRecord[];
  isLoadingSourceClasses: boolean;
  loadSourceTrimesterClasses: (trimesterId: string) => Promise<void>;
}

const FIRST_TRIMESTER_ID = '';

const buildLabel = (number: number, year: number) => `${number}º Trimestre ${year}`;

const initialTrimesters: Trimester[] = [];

const initialStudentsByTrimester: Record<string, ClassStudentRecord[]> = {};

const initialVisitorsByTrimester: Record<string, ClassVisitorRecord[]> = {};

const buildLessonsForClass = (classId: string): ClassLesson[] =>
  Array.from({ length: LESSONS_PER_TRIMESTER }, (_, index) => ({
    id: buildLessonId(classId, index + 1),
    classId,
    lessonNumber: index + 1,
    title: '',
    date: '',
    description: '',
  }));

const buildLessonsByClass = (classItems: Array<{ id: string }>): Record<string, ClassLesson[]> =>
  classItems.reduce<Record<string, ClassLesson[]>>((accumulator, classItem) => {
    accumulator[classItem.id] = buildLessonsForClass(classItem.id);

    return accumulator;
  }, {});

const initialClassLessonsByTrimester: Record<string, Record<string, ClassLesson[]>> = {};

const initialAttendanceEntriesByTrimester: Record<string, AttendanceEntry[]> = {};

const TrimestersContext = createContext<TrimestersContextValue | null>(null);

const cloneStudents = (students: ClassStudentRecord[]): ClassStudentRecord[] =>
  students.map((student) => ({
    ...student,
    classHistory: student.classHistory.map((entry) => ({ ...entry })),
  }));

export const TrimestersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentChurchId } = useAuth();
  const [trimesters, setTrimesters] = useState<Trimester[]>(initialTrimesters);
  const [classes, setClasses] = useState<ClassRecord[]>(classSeed);
  const [studentsByTrimester, setStudentsByTrimester] =
    useState<Record<string, ClassStudentRecord[]>>(initialStudentsByTrimester);
  const [visitorsByTrimester, setVisitorsByTrimester] =
    useState<Record<string, ClassVisitorRecord[]>>(initialVisitorsByTrimester);
  const [classLessonsByTrimester, setClassLessonsByTrimester] =
    useState<Record<string, Record<string, ClassLesson[]>>>(initialClassLessonsByTrimester);
  const [attendanceEntriesByTrimester, setAttendanceEntriesByTrimester] =
    useState<Record<string, AttendanceEntry[]>>(initialAttendanceEntriesByTrimester);
  const [sourceTrimesterClasses, setSourceTrimesterClasses] = useState<ClassRecord[]>([]);
  const [isLoadingSourceClasses, setIsLoadingSourceClasses] = useState(false);
  const [currentTrimesterId, setCurrentTrimesterId] = useState<string>(FIRST_TRIMESTER_ID);
  const [isLoadingTrimesters, setIsLoadingTrimesters] = useState(true);
  const [trimestersError, setTrimestersError] = useState<string | null>(null);

  const currentTrimester = useMemo(
    () =>
      trimesters.find((item) => item.id === currentTrimesterId) ??
      trimesters[0] ?? {
        id: '',
        year: new Date().getFullYear(),
        number: 1 as const,
        label: '',
        startDate: '',
        endDate: '',
        active: false,
        magazinesCount: 0,
        classQuartersCount: 0,
      },
    [currentTrimesterId, trimesters]
  );

  const reloadTrimesters = useCallback(async () => {
    if (!currentChurchId) {
      setIsLoadingTrimesters(false);
      return;
    }

    setIsLoadingTrimesters(true);
    setTrimestersError(null);

    try {
      const items = await trimestersService.list();
      setTrimesters(items);
      setCurrentTrimesterId((current) => {
        if (items.some((item) => item.id === current)) {
          return current;
        }

        return items.find((item) => item.active)?.id ?? items[0]?.id ?? FIRST_TRIMESTER_ID;
      });
    } catch (error) {
      setTrimestersError(error instanceof Error ? error.message : 'Nao foi possivel carregar os trimestres.');
    } finally {
      setIsLoadingTrimesters(false);
    }
  }, [currentChurchId]);

  useEffect(() => {
    reloadTrimesters();
  }, [reloadTrimesters]);

  const getStudents = useCallback(
    (trimesterId: string) => studentsByTrimester[trimesterId] ?? [],
    [studentsByTrimester]
  );

  const createClass = useCallback((classItem: ClassRecord) => {
    setClasses((current) => [classItem, ...current]);
    setClassLessonsByTrimester((current) => {
      const next = { ...current };

      Object.keys(next).forEach((trimesterId) => {
        next[trimesterId] = {
          ...next[trimesterId],
          [classItem.id]: Array.from({ length: LESSONS_PER_TRIMESTER }, (_, index) => ({
            id: buildLessonId(classItem.id, index + 1),
            classId: classItem.id,
            lessonNumber: index + 1,
            title: '',
            date: '',
            description: '',
          })),
        };
      });

      return next;
    });
  }, []);

  const updateClass = useCallback((classId: string, updates: Partial<ClassRecord>) => {
    setClasses((current) => current.map((item) => (item.id === classId ? { ...item, ...updates } : item)));
  }, []);

  const removeClass = useCallback((classId: string) => {
    setClasses((current) => current.filter((item) => item.id !== classId));
    setClassLessonsByTrimester((current) => {
      const next = { ...current };

      Object.keys(next).forEach((trimesterId) => {
        const trimesterLessons = { ...next[trimesterId] };
        delete trimesterLessons[classId];
        next[trimesterId] = trimesterLessons;
      });

      return next;
    });
  }, []);

  const students = useMemo(() => studentsByTrimester[currentTrimesterId] ?? [], [currentTrimesterId, studentsByTrimester]);
  const visitors = useMemo(() => visitorsByTrimester[currentTrimesterId] ?? [], [currentTrimesterId, visitorsByTrimester]);
  const attendanceEntries = useMemo(
    () => attendanceEntriesByTrimester[currentTrimesterId] ?? [],
    [attendanceEntriesByTrimester, currentTrimesterId]
  );

  const setStudents = useCallback<React.Dispatch<React.SetStateAction<ClassStudentRecord[]>>>(
    (updater) => {
      setStudentsByTrimester((current) => {
        const existing = current[currentTrimesterId] ?? [];
        const next = typeof updater === 'function' ? (updater as (prev: ClassStudentRecord[]) => ClassStudentRecord[])(existing) : updater;
        return { ...current, [currentTrimesterId]: next };
      });
    },
    [currentTrimesterId]
  );

  const setVisitors = useCallback<React.Dispatch<React.SetStateAction<ClassVisitorRecord[]>>>(
    (updater) => {
      setVisitorsByTrimester((current) => {
        const existing = current[currentTrimesterId] ?? [];
        const next = typeof updater === 'function' ? (updater as (prev: ClassVisitorRecord[]) => ClassVisitorRecord[])(existing) : updater;
        return { ...current, [currentTrimesterId]: next };
      });
    },
    [currentTrimesterId]
  );

  const setAttendanceEntries = useCallback<React.Dispatch<React.SetStateAction<AttendanceEntry[]>>>(
    (updater) => {
      setAttendanceEntriesByTrimester((current) => {
        const existing = current[currentTrimesterId] ?? [];
        const next = typeof updater === 'function' ? (updater as (prev: AttendanceEntry[]) => AttendanceEntry[])(existing) : updater;
        return { ...current, [currentTrimesterId]: next };
      });
    },
    [currentTrimesterId]
  );

  const updateStudent = useCallback(
    (studentId: string, updater: (student: ClassStudentRecord) => ClassStudentRecord) => {
      setStudents((currentStudents) =>
        currentStudents.map((student) => (student.id === studentId ? updater(student) : student))
      );
    },
    [setStudents]
  );

  const updateVisitor = useCallback(
    (visitorId: string, updater: (visitor: ClassVisitorRecord) => ClassVisitorRecord) => {
      setVisitors((currentVisitors) =>
        currentVisitors.map((visitor) => (visitor.id === visitorId ? updater(visitor) : visitor))
      );
    },
    [setVisitors]
  );

  const loadSourceTrimesterClasses = useCallback(async (trimesterId: string) => {
    if (!trimesterId) {
      setSourceTrimesterClasses([]);
      return;
    }

    setIsLoadingSourceClasses(true);
    try {
      const classes = await trimestersService.getClasses(trimesterId);
      setSourceTrimesterClasses(classes);
    } catch (error) {
      console.error('Erro ao carregar classes do trimestre fonte:', error);
      setSourceTrimesterClasses([]);
    } finally {
      setIsLoadingSourceClasses(false);
    }
  }, []);

  const createTrimester = useCallback<(input: CreateTrimesterInput) => Promise<string>>(
    async ({ year, number, startDate, endDate, replicateFromId, excludedStudentIds, replicationOptions }) => {
      setTrimestersError(null);
      const trimester = await trimestersService.create({
        year,
        number,
        startDate,
        endDate,
        active: true,
      });
      const id = trimester.id;

      setTrimesters((current) => [...current, trimester]);

      // Se há trimestre para replicar, chamar API de replicação
      if (replicateFromId) {
        try {
          await trimestersService.replicate(replicateFromId, id, {
            include_classes: true,
            include_magazines: true,
            include_students: replicationOptions?.includeStudents ?? true,
            excluded_student_ids: excludedStudentIds,
            selected_class_ids: replicationOptions?.selectedClassIds,
            include_teachers: replicationOptions?.includeTeachers ?? true,
          });
        } catch (error) {
          console.error('Erro ao replicar trimestre:', error);
          // Não falhar a criação, apenas logar o erro
        }
      }

      setCurrentTrimesterId(id);
      return id;
    },
    []
  );

  const updateTrimester = useCallback(async (id: string, updates: Partial<Omit<Trimester, 'id'>>) => {
    const existing = trimesters.find((item) => item.id === id);
    if (!existing) return;

    setTrimestersError(null);

    const merged = {
      ...existing,
      ...updates,
      label: buildLabel(updates.number ?? existing.number, updates.year ?? existing.year),
    };
    const saved = await trimestersService.update(id, {
      year: merged.year,
      number: merged.number,
      startDate: merged.startDate,
      endDate: merged.endDate,
      active: merged.active,
    });

    setTrimesters((current) => current.map((item) => (item.id === id ? saved : item)));
  }, [trimesters]);

  const removeTrimesterLocally = useCallback(
    (id: string) => {
      setTrimesters((current) => {
        const next = current.filter((item) => item.id !== id);

        if (currentTrimesterId === id) {
          setCurrentTrimesterId(next[0]?.id ?? FIRST_TRIMESTER_ID);
        }

        return next;
      });

      setStudentsByTrimester((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });

      setClassLessonsByTrimester((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });

      setAttendanceEntriesByTrimester((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });

      setVisitorsByTrimester((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
    },
    [currentTrimesterId]
  );

  const deleteTrimester = useCallback(async (id: string) => {
    setTrimestersError(null);
    await trimestersService.delete(id);
    removeTrimesterLocally(id);
  }, [removeTrimesterLocally]);

  const removeReplicatedStudent = useCallback((trimesterId: string, studentId: string) => {
    setStudentsByTrimester((current) => {
      const existing = current[trimesterId] ?? [];
      return { ...current, [trimesterId]: existing.filter((student) => student.id !== studentId) };
    });
  }, []);

  const getClassLessons = useCallback(
    (classId: string, trimesterId = currentTrimesterId) =>
      classLessonsByTrimester[trimesterId]?.[classId] ?? buildLessonsForClass(classId),
    [classLessonsByTrimester, currentTrimesterId]
  );

  const updateClassLesson = useCallback(
    (classId: string, lessonId: string, updates: Partial<Omit<ClassLesson, 'id' | 'classId' | 'lessonNumber'>>) => {
      setClassLessonsByTrimester((current) => {
        const trimesterLessons = current[currentTrimesterId] ?? {};
        const classLessons = trimesterLessons[classId] ?? buildLessonsForClass(classId);

        return {
          ...current,
          [currentTrimesterId]: {
            ...trimesterLessons,
            [classId]: classLessons.map((lesson) => (lesson.id === lessonId ? { ...lesson, ...updates } : lesson)),
          },
        };
      });
    },
    [currentTrimesterId]
  );

  const value = useMemo<TrimestersContextValue>(
    () => ({
      trimesters,
      currentTrimesterId,
      currentTrimester,
      setCurrentTrimesterId,
      createTrimester,
      updateTrimester,
      deleteTrimester,
      isLoadingTrimesters,
      trimestersError,
      reloadTrimesters,
      classes,
      setClasses,
      createClass,
      updateClass,
      removeClass,
      studentsByTrimester,
      getStudents,
      students,
      setStudents,
      updateStudent,
      visitorsByTrimester,
      visitors,
      setVisitors,
      updateVisitor,
      removeReplicatedStudent,
      attendanceEntriesByTrimester,
      attendanceEntries,
      setAttendanceEntries,
      classLessonsByTrimester,
      getClassLessons,
      updateClassLesson,
      sourceTrimesterClasses,
      isLoadingSourceClasses,
      loadSourceTrimesterClasses,
    }),
    [
      trimesters,
      currentTrimesterId,
      currentTrimester,
      createTrimester,
      updateTrimester,
      deleteTrimester,
      isLoadingTrimesters,
      trimestersError,
      reloadTrimesters,
      classes,
      setClasses,
      createClass,
      updateClass,
      removeClass,
      studentsByTrimester,
      getStudents,
      students,
      setStudents,
      updateStudent,
      visitorsByTrimester,
      visitors,
      setVisitors,
      updateVisitor,
      removeReplicatedStudent,
      attendanceEntriesByTrimester,
      attendanceEntries,
      setAttendanceEntries,
      classLessonsByTrimester,
      getClassLessons,
      updateClassLesson,
    ]
  );

  return <TrimestersContext.Provider value={value}>{children}</TrimestersContext.Provider>;
};

export const useTrimesters = () => {
  const ctx = useContext(TrimestersContext);

  if (!ctx) {
    throw new Error('useTrimesters must be used within TrimestersProvider');
  }

  return ctx;
};

export const useClassStudents = () => {
  const { students, setStudents, updateStudent } = useTrimesters();
  return { students, setStudents, updateStudent };
};
